import React, { useState, useEffect } from "react";
import { useGetHelpDataByCustomerQuery } from "../../Services/HelpService";
import { useSelector } from "react-redux";

const MyTickets = () => {
  const authData = useSelector((state: any) => state.AuthSlice);
  const customerId = authData?.authUser?._id;
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [ticketsData, setTicketsData] = useState<any>([]);

  const {
    data: helpData,
    isLoading,
    isSuccess,
    isError,
    refetch,
  } = useGetHelpDataByCustomerQuery(
    { customerId, page, pageSize },
    { skip: !customerId }
  );

  useEffect(() => {
    if (customerId) {
      refetch(); // <--- forces fresh data load
    }
  }, [customerId, refetch]);

  const HelpTicketsSection: any = [
    { label: "Active", value: "active" },
    { label: "Closed", value: "closed" },
    { label: "Awaiting Response from Customer", value: "awaiting_customer" },
    { label: "Hold", value: "on_hold" },
  ];

  const handleSortedTickets = () => {
    if (helpData?.tickets?.length > 0) {
      setTicketsData({
        ...helpData,
      });
    } else {
      setTicketsData(helpData);
    }
  };

  useEffect(() => {
    handleSortedTickets();
  }, [helpData]);

  const handleChangeRowsPerPage = (e: any) => {
    setPageSize(parseInt(e.target.value));
    setPage(1);
  };

  const statusColorMap: Record<string, string> = {
    active: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300",
    closed: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300",
    awaiting_customer: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300",
    on_hold: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300",
  };

  return (
    <div className="overflow-x-auto bg-white dark:bg-dark-foreground rounded-lg shadow mt-5">
      <div className="text-center p-6">
        <h1 className="text-center text-2xl font-bold dark:text-white">
          My Tickets
        </h1>
      </div>
      <table className="min-w-full text-sm text-left text-gray-800 dark:text-gray-200">
        <thead className="bg-gray-300 dark:bg-dark-background text-gray-700 dark:text-gray-300 font-semibold">
          <tr>
            <th className="px-4 py-2">#</th>
            <th className="px-4 py-2">Service Name</th>
            <th className="px-4 py-2">Selected Question</th>
            <th className="px-4 py-2">Order</th>
            <th className="px-4 py-2">Comments</th>
            <th className="px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={7} className="text-center py-4 text-gray-500 dark:text-gray-400">
                Loading...
              </td>
            </tr>
          ) : ticketsData?.tickets && ticketsData?.tickets.length > 0 ? (
            ticketsData?.tickets?.map((item: any, index: number) => (
              <tr
                key={item.id || index}
                className="border-b dark:border-dark-foreground hover:bg-gray-50 dark:hover:bg-dark-background transition"
              >
                <td className="px-4 py-2">
                  {(page - 1) * pageSize + index + 1}
                </td>
                <td className="px-4 py-2">{item.serviceName || "-"}</td>
                {/*
                  A dash, not an empty cell.

                  A query raised under "Something not listed", or about a
                  specific order, has no question behind it -- there was no
                  list to pick one from. A blank cell reads as data that went
                  missing rather than a question that was never asked.
                */}
                <td className="px-4 py-2">{item.selectedQuestion || "-"}</td>
                {/* Which order this was about, when it named one. */}
                <td className="px-4 py-2 whitespace-nowrap">
                  {item.orderNumber ? (
                    <span>
                      {item.orderNumber}
                      {item.itemNumber && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {item.itemNumber}
                        </span>
                      )}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-4 py-2 truncate max-w-xs">
                  <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.comments}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-block px-2 py-1 text-sm text-center rounded font-medium  ${
                      statusColorMap[item.status] || "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                    }`}
                  >
                    {HelpTicketsSection.find(
                      (s: { value: string }) => s.value === item.status
                    )?.label || item.status}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center py-4 text-gray-500 dark:text-gray-400">
                No tickets found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-t dark:border-dark-foreground text-sm text-gray-700 dark:text-gray-300">
        <div>
          Rows per page:
          <select
            value={pageSize}
            onChange={handleChangeRowsPerPage}
            className="ml-2 px-2 py-1 border dark:border-dark-foreground rounded bg-white dark:bg-dark-background dark:text-white"
          >
            {[5, 10, 15].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-2 py-1 rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-dark-background border border-gray-800 dark:border-dark-foreground dark:text-gray-300"
          >
            Prev
          </button>
          {/* <span>
            Page {page} of {Math.ceil(helpData?.totalCount / pageSize)}
          </span> */}
          <span>
            Page {page} of{" "}
            {helpData?.totalCount
              ? Math.ceil(helpData.totalCount / pageSize)
              : 1}
          </span>

          <button
            onClick={() => setPage(page + 1)}
            // disabled={page >= helpData?.totalPages}
            disabled={!helpData?.totalPages || page >= helpData.totalPages}
            className="px-2 py-1 rounded disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-dark-background border border-gray-800 dark:border-dark-foreground dark:text-gray-300"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyTickets;
